import { Terminal } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { baseHref } from '@/portainer/helpers/pathHelper';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { Button } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

interface Props {
  environmentId: EnvironmentId;
  nodeName: string;
  isNodeShellEnabled: boolean;
}

const DISABLED_TOOLTIP =
  'Node shell for admins is disabled. Enable it in Cluster > Setup.';

export function NodeShellActionButton({
  environmentId,
  nodeName,
  isNodeShellEnabled,
}: Props) {
  const isAdmin = useIsPureAdmin();

  if (!isAdmin) {
    return null;
  }

  if (!isNodeShellEnabled) {
    return (
      <TooltipWithChildren message={DISABLED_TOOLTIP} position="top">
        <Button
          color="none"
          size="small"
          icon={Terminal}
          className="text-blue-8 th-highcontrast:text-white th-dark:text-blue-7"
          aria-label={`Node shell on ${nodeName} (disabled)`}
          data-cy="nodeShellButton"
          disabled
        />
      </TooltipWithChildren>
    );
  }

  return (
    <TooltipWithChildren message="Shell" position="top">
      <Button
        color="none"
        size="small"
        icon={Terminal}
        className="text-blue-8 th-highcontrast:text-white th-dark:text-blue-7"
        aria-label={`Open shell on node ${nodeName}`}
        data-cy="nodeShellButton"
        onClick={() => openNodeShell(environmentId, nodeName)}
      />
    </TooltipWithChildren>
  );
}

function openNodeShell(environmentId: EnvironmentId, nodeName: string) {
  const url = window.location.origin + baseHref();
  const windowName = `node-shell-${environmentId}-${uuidv4()}`;
  const encodedNode = encodeURIComponent(nodeName);
  window.open(
    `${url}#!/${environmentId}/kubernetes/node-shell?nodeName=${encodedNode}`,
    windowName,
    'width=800,height=600'
  );
}
